import { useEffect } from 'react';

/**
 * usePrintFitCharts hook.
 * The line charts of a report are drawn for the screen width (nivo measures its container; no JS runs while the
 * browser lays the page out for print), and the print rule `svg { max-width: 100% }` cut them at the page edge -
 * the last month, the report month, was lost. Before printing (the download button calls `window.print()`, the same
 * as Ctrl+P) every chart marked `data-print-fit` gets a `viewBox` of its current size, so the print styles scale it
 * to the page width (`normalize.scss`); after printing the `viewBox` is removed (a later resize must not stretch it).
 *
 * @category hooks.
 */
const usePrintFitCharts = (): void => {
  useEffect(() => {
    const charts = () =>
      [...document.querySelectorAll('[data-print-fit]')]
        .map((box) => box.querySelector('svg'))
        .filter((svg): svg is SVGSVGElement => !!svg);

    const fit = () =>
      charts().forEach((svg) => {
        const width = svg.getAttribute('width');
        const height = svg.getAttribute('height');
        if (width && height && !svg.hasAttribute('viewBox')) svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      });
    const unfit = () => charts().forEach((svg) => svg.removeAttribute('viewBox'));

    window.addEventListener('beforeprint', fit);
    window.addEventListener('afterprint', unfit);

    return () => {
      window.removeEventListener('beforeprint', fit);
      window.removeEventListener('afterprint', unfit);
    };
  }, []);
};

export { usePrintFitCharts };
